/**
 * PATRONS de tissu des trousses 3D : pour chaque forme, UNE image (2048 × 1024, 2:1) où chaque face
 * de la trousse a sa zone. L'admin télécharge le patron de la forme, le peint, puis l'importe : la
 * scène pose chaque zone sur sa face (2e jeu d'UV, `uv1`, calculé ici).
 *
 *   boîte / berlingot (projection par face) :      tube (déroulé) :
 *   ┌────────────┬────────────┐                    ┌─────────────────────────┐
 *   │ FACE AVANT │    DOS     │  v 0,5 → 1         │ dos                     │
 *   ├────────────┼────────────┤                    │ dessus (zip)            │  v 0,25 → 1
 *   │  DESSUS    │  DESSOUS   │  v 0,25 → 0,5      │ devant                  │
 *   ├─────┬──────┼────────────┤                    │ dessous                 │
 *   │BOUT │ BOUT │ non utilisé│  v 0 → 0,25        ├─────────────────────────┤
 *   │ G.  │  D.  │            │                    │ non utilisé (bouts = logo)
 *   └─────┴──────┴────────────┘                    └─────────────────────────┘
 *
 * Coordonnées normalisées, v vers le HAUT (convention three.js : le haut de l'image = v 1). PUR, sauf
 * `patronCanvas` (dessin du gabarit, navigateur).
 */
import type { TrousseShape } from './shapes';

export const PATRON_WIDTH = 2048;
export const PATRON_HEIGHT = 1024;

export interface PatronRegion {
  id: string;
  label: string;
  /** u0, v0 (coin bas gauche), u1, v1 — normalisés */
  rect: [number, number, number, number];
}

const BOX_REGIONS: PatronRegion[] = [
  { id: 'front', label: 'FACE AVANT', rect: [0, 0.5, 0.5, 1] },
  { id: 'back', label: 'DOS', rect: [0.5, 0.5, 1, 1] },
  { id: 'top', label: 'DESSUS', rect: [0, 0.25, 0.5, 0.5] },
  { id: 'bottom', label: 'DESSOUS', rect: [0.5, 0.25, 1, 0.5] },
  { id: 'left', label: 'BOUT GAUCHE', rect: [0, 0, 0.25, 0.25] },
  { id: 'right', label: 'BOUT DROIT', rect: [0.25, 0, 0.5, 0.25] },
];

const TUBE_REGIONS: PatronRegion[] = [{ id: 'body', label: 'TOUR DU TUBE', rect: [0, 0.25, 1, 1] }];

export function patronRegions(shape: TrousseShape): PatronRegion[] {
  return shape === 'tube' ? TUBE_REGIONS : BOX_REGIONS;
}

export interface Bounds {
  min: [number, number, number];
  max: [number, number, number];
}

const clamp01 = (t: number) => Math.min(1, Math.max(0, t));
const lerp = (a: number, b: number, t: number) => a + (b - a) * clamp01(t);

/**
 * Projection « par face » (boîte, berlingot) : la face est choisie par l'axe dominant de la
 * normale, puis le point est posé dans la zone de cette face. Chaque face se lit à l'endroit vue
 * de l'extérieur (le dos est retourné, le dessus a l'avant en bas). PUR.
 */
export function boxPatronUV(
  normal: [number, number, number],
  p: [number, number, number],
  b: Bounds,
): [number, number] {
  const [nx, ny, nz] = normal;
  const [x, y, z] = p;
  const fx = (x - b.min[0]) / (b.max[0] - b.min[0] || 1);
  const fy = (y - b.min[1]) / (b.max[1] - b.min[1] || 1);
  const fz = (z - b.min[2]) / (b.max[2] - b.min[2] || 1);
  const ax = Math.abs(nx);
  const ay = Math.abs(ny);
  const az = Math.abs(nz);
  const at = (id: string, u: number, v: number): [number, number] => {
    const r = BOX_REGIONS.find((g) => g.id === id)!.rect;
    return [lerp(r[0], r[2], u), lerp(r[1], r[3], v)];
  };
  if (az >= ax && az >= ay) return nz >= 0 ? at('front', fx, fy) : at('back', 1 - fx, fy);
  if (ay >= ax) return ny >= 0 ? at('top', fx, 1 - fz) : at('bottom', fx, fz);
  return nx < 0 ? at('left', fz, fy) : at('right', 1 - fz, fy);
}

/**
 * Tube déroulé : `along` 0..1 de gauche à droite, `around` 0..1 en partant du dessous et en passant
 * par le devant, le dessus puis le dos. PUR.
 */
export function tubePatronUV(along: number, around: number): [number, number] {
  return [clamp01(along), 0.25 + 0.75 * clamp01(around)];
}

/* ── le gabarit à télécharger ─────────────────────────────────────────────── */

const SHAPE_TITLE: Record<TrousseShape, string> = {
  boite: 'Patron — trousse boîte',
  berlingot: 'Patron — trousse berlingot',
  tube: 'Patron — trousse tube',
};

/** Dessine le patron d'une forme : zones, noms, repères (zip, bandes du tube). Navigateur. */
export function patronCanvas(shape: TrousseShape): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = PATRON_WIDTH;
  c.height = PATRON_HEIGHT;
  const x = c.getContext('2d');
  if (!x) return c;
  const W = PATRON_WIDTH;
  const H = PATRON_HEIGHT;
  // normalisé (v vers le haut) → pixels
  const px = (u: number) => u * W;
  const py = (v: number) => (1 - v) * H;

  x.fillStyle = '#d9dde6';
  x.fillRect(0, 0, W, H);
  x.textAlign = 'center';
  x.textBaseline = 'middle';

  const regions = patronRegions(shape);
  const used = (u: number, v: number) =>
    regions.some((r) => u >= r.rect[0] && u <= r.rect[2] && v >= r.rect[1] && v <= r.rect[3]);

  // zone non utilisée, hachurée
  x.save();
  x.fillStyle = '#b7bdca';
  for (let i = -H; i < W; i += 28) {
    x.beginPath();
    x.moveTo(i, H);
    x.lineTo(i + H, 0);
    x.lineTo(i + H + 10, 0);
    x.lineTo(i + 10, H);
    x.closePath();
    x.fill();
  }
  x.restore();

  for (const r of regions) {
    const [u0, v0, u1, v1] = r.rect;
    x.fillStyle = '#ffffff';
    x.fillRect(px(u0), py(v1), px(u1) - px(u0), py(v0) - py(v1));
    x.setLineDash([18, 12]);
    x.strokeStyle = '#0566f2';
    x.lineWidth = 5;
    x.strokeRect(px(u0) + 3, py(v1) + 3, px(u1) - px(u0) - 6, py(v0) - py(v1) - 6);
    x.setLineDash([]);
    x.fillStyle = '#0566f2';
    x.font = '800 54px Outfit, Inter, sans-serif';
    x.fillText(r.label, (px(u0) + px(u1)) / 2, (py(v0) + py(v1)) / 2);
  }

  x.font = '600 30px Inter, sans-serif';
  x.fillStyle = '#c0392b';
  x.strokeStyle = '#c0392b';
  x.lineWidth = 4;
  const guide = (v: number, u0: number, u1: number, label: string) => {
    x.setLineDash([10, 10]);
    x.beginPath();
    x.moveTo(px(u0) + 10, py(v));
    x.lineTo(px(u1) - 10, py(v));
    x.stroke();
    x.setLineDash([]);
    x.textAlign = 'left';
    x.fillText(label, px(u0) + 24, py(v) - 22);
    x.textAlign = 'center';
  };

  if (shape === 'boite') {
    // la fermeture éclair fait le tour à 66 % de la hauteur (base 0,86 / 1,3)
    const zip = 0.5 + 0.5 * (0.86 / 1.3);
    guide(zip, 0, 0.5, 'fermeture éclair');
    guide(zip, 0.5, 1, 'fermeture éclair');
  } else if (shape === 'berlingot') {
    x.fillText('arête du zip = haut de la face', px(0.25), py(0.97));
  } else {
    const band = (a: number) => 0.25 + 0.75 * a;
    guide(band(0.125), 0, 1, 'dessous ↑ devant');
    guide(band(0.375), 0, 1, 'devant ↑ dessus');
    guide(band(0.5), 0, 1, 'fermeture éclair');
    guide(band(0.625), 0, 1, 'dessus ↑ dos');
    guide(band(0.875), 0, 1, 'dos ↑ dessous');
    x.fillStyle = '#4a5160';
    x.fillText('les deux bouts portent le logo JeRaisonne', px(0.5), py(0.12));
  }

  x.fillStyle = '#4a5160';
  x.font = '700 30px Inter, sans-serif';
  x.textAlign = 'right';
  const corner = used(0.99, 0.01) ? py(0.97) : py(0.06);
  x.fillText(`${SHAPE_TITLE[shape]} · ${W} × ${H} px`, W - 24, corner);
  return c;
}
